import { FALLBACK_CURRENCY, mapMoney, numberOr, sumMoney } from "./money";

export const buildOrderInfoPayload = ({
  cartId,
  deliveryTypeId,
  deliveryOn,
  deliveryAddress,
  couponCode,
}) => ({
  cartId,
  deliveryTypeId,
  ...(deliveryOn ? { deliveryOn } : {}),
  ...(deliveryAddress ? { deliveryAddress } : {}),
  ...(couponCode ? { couponCode } : {}),
});

export const mapOrderInfo = (dto) => {
  if (!dto) return null;

  const cartInfo = dto.cartInfo || null;
  const payableAmount = mapMoney(dto.payableAmount) || mapMoney(dto.totalAmount);
  const totalAmount = mapMoney(dto.totalAmount) || mapMoney(dto.payableAmount);

  const totalVAT = sumMoney([
    mapMoney(cartInfo?.productsAmountVAT),
    mapMoney(dto.deliveryAmountVAT),
    mapMoney(dto.platformFeeVAT),
  ]);

  return {
    cartId: cartInfo?.cartId ?? null,
    subTotal: mapMoney(dto.subTotal) || mapMoney(cartInfo?.productsAmount),
    discount: mapMoney(dto.discountAmount) || mapMoney(cartInfo?.couponDiscount),
    deliveryFee: mapMoney(dto.applicableDeliveryAmount) || mapMoney(dto.deliveryAmount),
    platformFee: mapMoney(dto.platformFee),
    totalVAT,
    payableAmount,
    totalAmount,
    codAmount: mapMoney(dto.codAmount),
    currency:
      dto.currency?.code || payableAmount?.currency || totalAmount?.currency || FALLBACK_CURRENCY,
    couponCode: dto.couponCode || cartInfo?.coupon?.code || null,
    couponValid: dto.couponValid !== false,
    couponMessage: dto.couponMessage || null,
    distanceInMeters: numberOr(dto.distanceInMeters),
  };
};

export const mapReservation = (dto) => {
  if (!dto?.id) return null;

  const orderInfo = mapOrderInfo(dto.orderInfo);

  return {
    pendingOrderId: dto.id,
    orderInfo,
    payableAmount: orderInfo?.payableAmount ?? null,
    expiresAt: dto.expiresAt || null,
  };
};

export const sumOrderInfos = (infos) => {
  const list = (infos || []).filter(Boolean);

  if (!list.length) return null;

  return {
    subTotal: sumMoney(list.map((info) => info.subTotal)),
    discount: sumMoney(list.map((info) => info.discount)),
    deliveryFee: sumMoney(list.map((info) => info.deliveryFee)),
    platformFee: sumMoney(list.map((info) => info.platformFee)),
    totalVAT: sumMoney(list.map((info) => info.totalVAT)),
    payableAmount: sumMoney(list.map((info) => info.payableAmount)),
    totalAmount: sumMoney(list.map((info) => info.totalAmount)),
    currency: list[0].currency,
    couponCode: list.find((info) => info.couponCode)?.couponCode ?? null,
    couponMessage: list.find((info) => info.couponMessage)?.couponMessage ?? null,
    couponValid: list.every((info) => info.couponValid !== false),
  };
};

export const mapPlacedOrder = (dto) => {
  if (!dto?.id) return null;

  return {
    id: dto.id,
    orderNumber: dto.orderNumber || String(dto.id),
    status: (dto.currentStatus || dto.status || "").toUpperCase() || "PENDING",
  };
};

export const mapMasterReservation = (dto) => {
  if (!dto?.id) return null;

  const perSupplier = (dto.orders || []).map(mapOrderInfo);

  return {
    pendingOrderId: dto.id,
    payableAmount: mapMoney(dto.total) || sumMoney(perSupplier.map((info) => info.payableAmount)),
    expiresAt: dto.expiresAt || null,
    perSupplier,
  };
};

export const mapMasterPlacedOrder = (dto) => {
  if (!dto?.id) return null;

  return {
    id: dto.id,
    orderNumber: dto.orderNumber || String(dto.id),
    status: (dto.currentStatus || dto.status || "").toUpperCase() || "PENDING",
    subOrderCount: numberOr(dto.subOrderCount) ?? (dto.subOrders || []).length,
  };
};
